import { fb_remote_config } from '@/utils/firebase.js';

// localStorage cache keys
const CACHE_KEY_UFB_ENABLED = 'firebase_remote_config_ufb_enabled';
const CACHE_EXPIRY_MS = 60000; // 1 minute (matches Firebase Remote Config cache)
const VALID_SIM_TYPES = ['esim', 'psim'];
const FALLBACK_SIM_TYPES = 'esim';

const VALID_SSO_TYPES = ['google', 'apple'];
const FALLBACK_SSO_TYPES = 'none';

const isValidSsoTypes = (value) => {
    if (typeof value !== 'string' || !value.trim()) return false
    const normalized = value.trim().toLowerCase()
    if (normalized === 'none') return true
    const parts = normalized.split(',').map(t => t.trim()).filter(Boolean)
    if (parts.length < 1 || parts.length > 2) return false
    if (!parts.every(t => VALID_SSO_TYPES.includes(t))) return false
    if (new Set(parts).size !== parts.length) return false
    return true
};

// Helper to get cached value from localStorage
const getCachedValue = (key, defaultValue) => {
    try {
        const cached = localStorage.getItem(key);
        if (cached !== null) {
            const { value, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;

            // Check if cache is still valid
            if (age < CACHE_EXPIRY_MS) {
                return value;
            }
        }
    } catch (error) {
        // Ignore cache errors, return default
    }
    return defaultValue;
};

// Helper to set cached value in localStorage
const setCachedValue = (key, value) => {
    try {
        const cacheData = {
            value,
            timestamp: Date.now()
        };
        localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
        // Ignore cache write errors
    }
};

// Initialize state with cached value
const initialUfbEnabled = getCachedValue(CACHE_KEY_UFB_ENABLED, false);

const isValidSimTypes = (value) => {
    if (typeof value !== 'string' || !value.trim()) return false
    const parts = value.split(',').map(t => t.trim().toLowerCase())
    if (parts.length < 1 || parts.length > 2) return false
    if (!parts.every(t => VALID_SIM_TYPES.includes(t))) return false
    if (new Set(parts).size !== parts.length) return false
    return true
}

export default {
    state: {
        is_hide_prepay_topup: true,
        is_hide_prepay_balance_if_zero: true,
        is_afterpay_payment_module_allowed_fb: false,
        is_enable_promo_code_menu_item: false,
        is_klarna_payment_module_allowed: false,
        show_addon_images: true,
        show_carryover: false,
        // Initialize from localStorage cache to prevent 404 flash on page load
        is_module_ufb_enabled: initialUfbEnabled,
        show_dashboard_widget: "Hotspot,MMS,Data,Voice,SMS",
        show_hotspot: false,
        ufb_change_plan_mode: "LATER", // NOW, LATER, BOTH Default value - LATER as requested
        show_plans_password_popup: false,
        password_style: 1,
        enable_invoice_line_period: false,
        invoice_show_invoice_period: false,
        // POI Document Type Visibility - default to true - placeholder for future
        poi_show_driver_license: true,
        poi_show_passport: true,
        is_show_esim_download_url: false,
        available_sim_types: FALLBACK_SIM_TYPES,
        sims_multi_plans_member_name: false,
        is_nickname_field_mandatory: false,
        is_trial_mode: false,
        show_esim_activation_tab: false,
        show_esim_download_tab_on_checkout: false,
        esim_download_style_on_checkout: 1,
        is_enable_purchase_plans_and_addons: true,
        is_travel_esim_enabled: false,
        available_sso_types: FALLBACK_SSO_TYPES,
        show_usage_breakdown: null,
    },

    getters: {
        is_hide_prepay_topup: (state) => state.is_hide_prepay_topup,
        is_hide_prepay_balance_if_zero: (state) => state.is_hide_prepay_balance_if_zero,
        is_afterpay_payment_module_allowed_fb: (state) => state.is_afterpay_payment_module_allowed_fb,
        is_enable_promo_code_menu_item: (state) => state.is_enable_promo_code_menu_item,
        is_klarna_payment_module_allowed: (state) => state.is_klarna_payment_module_allowed,
        show_addon_images: (state) => state.show_addon_images,
        show_carryover: (state) => state.show_carryover,
        is_module_ufb_enabled: (state) => state.is_module_ufb_enabled,
        show_dashboard_widget: (state) => state.show_dashboard_widget,
        show_hotspot: (state) => state.show_hotspot,
        ufb_change_plan_mode: (state) => state.ufb_change_plan_mode,
        show_plans_password_popup: (state) => state.show_plans_password_popup,
        password_style: (state) => state.password_style,
        enable_invoice_line_period: (state) => state.enable_invoice_line_period,
        invoice_show_invoice_period: (state) => state.invoice_show_invoice_period,
        // POI (Proof of Identity) Verification
        poi_show_driver_license: (state) => state.poi_show_driver_license,
        poi_show_passport: (state) => state.poi_show_passport,
        is_show_esim_download_url: (state) => state.is_show_esim_download_url,

        available_sim_types: (state) => state.available_sim_types,
        available_sim_types_list: (state) => state.available_sim_types.split(',').map(t => t.trim()).filter(Boolean),
        is_esim_available: (state, getters) => getters.available_sim_types_list.includes('esim'),
        is_psim_available: (state, getters) => getters.available_sim_types_list.includes('psim'),
        is_sim_type_toggle_visible: (state, getters) => getters.available_sim_types_list.length > 1,
        default_sim_type: (state, getters) => getters.available_sim_types_list[0] === 'esim' ? 'e_sim' : 'real_sim',

        sims_multi_plans_member_name: (state) => state.sims_multi_plans_member_name,
        is_nickname_field_mandatory: (state) => state.is_nickname_field_mandatory,
        is_trial_mode: (state) => state.is_trial_mode,
        is_airnz_trial: (state, getters, rootState, rootGetters) => rootGetters.theme_name === 'airnz' && state.is_trial_mode,
        show_esim_activation_tab: (state) => state.show_esim_activation_tab,
        show_esim_download_tab_on_checkout: (state) => state.show_esim_download_tab_on_checkout,
        esim_download_style_on_checkout: (state) => state.esim_download_style_on_checkout,
        is_enable_purchase_plans_and_addons: (state) => state.is_enable_purchase_plans_and_addons,
        is_travel_esim_enabled: (state) => state.is_travel_esim_enabled,

        available_sso_types: (state) => state.available_sso_types,
        available_sso_types_list: (state) => {
            const normalized = state.available_sso_types.trim().toLowerCase()
            if (normalized === 'none') return []
            return normalized.split(',').map(t => t.trim()).filter(Boolean)
        },
        is_google_sso_available: (state, getters) => getters.available_sso_types_list.includes('google'),
        is_apple_sso_available: (state, getters) => getters.available_sso_types_list.includes('apple'),
        is_any_sso_available: (state, getters) => getters.available_sso_types_list.length > 0,

        show_usage_breakdown: (state) => state.show_usage_breakdown,
    },

    actions: {
        get_is_hide_prepay_topup({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const is_hide_prepay_topup = fb_remote_config.getValue("hide_prepay_topup").asBoolean()

                commit('SET_IS_HIDE_PREPAY_TOPUP', is_hide_prepay_topup)

                return is_hide_prepay_topup
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.hide_prepay_topup

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_HIDE_PREPAY_TOPUP', defaultValue)
                
                return defaultValue
            })
        },

        get_is_hide_prepay_balance_if_zero({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const is_hide_prepay_balance_if_zero = fb_remote_config.getValue("hide_prepay_balance_if_zero").asBoolean()

                commit('SET_IS_HIDE_PREPAY_BALANCE_IF_ZERO', is_hide_prepay_balance_if_zero)
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.hide_prepay_balance_if_zero

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_HIDE_PREPAY_BALANCE_IF_ZERO', defaultValue)
                
                return defaultValue
            })
        },

        get_is_afterpay_payment_module_allowed({commit}) {
            fb_remote_config.fetchAndActivate().then(()=>{
                const is_afterpay_payment_module_allowed_fb = fb_remote_config.getValue("VUE_APP_IS_AFTERPAY_PAYMENT_MODULE_ALLOWED").asBoolean()
                commit('SET_IS_AFTERPAY_PAYMENT_MODULE_ALLOWED', is_afterpay_payment_module_allowed_fb)
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_IS_AFTERPAY_PAYMENT_MODULE_ALLOWED

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_AFTERPAY_PAYMENT_MODULE_ALLOWED', defaultValue)
                
                return defaultValue
            })
        },
        
        get_is_enable_promo_code_menu_item({commit}) {
            fb_remote_config.fetchAndActivate().then(()=>{
                const is_enable_promo_code_menu_item = fb_remote_config.getValue("VUE_APP_ENABLE_PROMO_CODE_MENU_ITEM").asBoolean()
                commit('SET_IS_ENABLE_PROMO_CODE_MENU_ITEM', is_enable_promo_code_menu_item)
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_ENABLE_PROMO_CODE_MENU_ITEM

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_ENABLE_PROMO_CODE_MENU_ITEM', defaultValue)
                
                return defaultValue
            })
        },

        get_show_addon_images({commit}) { 
            fb_remote_config.fetchAndActivate().then(() => {
                const show_addon_images = fb_remote_config.getValue("VUE_APP_CP_SHOW_ADDON_IMAGES").asBoolean()
                commit('SET_SHOW_ADDON_IMAGES', show_addon_images)
                return show_addon_images
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SHOW_ADDON_IMAGES

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_SHOW_ADDON_IMAGES', defaultValue)
                
                return defaultValue
            })
        },

        get_is_klarna_payment_module_allowed({commit}) {
            fb_remote_config.fetchAndActivate().then(()=>{
                const is_klarna_payment_module_allowed = fb_remote_config.getValue("VUE_APP_IS_KLARNA_PAYMENT_MODULE_ALLOWED").asBoolean()
                commit('SET_IS_KLARNA_PAYMENT_MODULE_ALLOWED', is_klarna_payment_module_allowed)
            }) 
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_IS_KLARNA_PAYMENT_MODULE_ALLOWED

                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_KLARNA_PAYMENT_MODULE_ALLOWED', defaultValue)
                return defaultValue
            })
        },
             
        get_show_carryover({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const show_carryover = fb_remote_config.getValue("VUE_APP_CP_SHOW_CARRYOVER_PARAM").asBoolean()
                commit('SET_SHOW_CARRYOVER', show_carryover)
                return show_carryover
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_SHOW_CARRYOVER_PARAM
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_SHOW_CARRYOVER', defaultValue)
                
                return defaultValue
            })
        },
        
        get_is_module_ufb_enabled({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_module_ufb_enabled = fb_remote_config.getValue("VUE_APP_CP_MODULE_UFB_ENABLED").asBoolean()
                commit('SET_IS_MODULE_UFB_ENABLED', is_module_ufb_enabled)

                // Cache the value in localStorage for page refreshes
                setCachedValue(CACHE_KEY_UFB_ENABLED, is_module_ufb_enabled)

                return is_module_ufb_enabled
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_MODULE_UFB_ENABLED
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_MODULE_UFB_ENABLED', defaultValue)

                // Cache the default value as well
                setCachedValue(CACHE_KEY_UFB_ENABLED, defaultValue)

                return defaultValue
            })
        },

        get_ufb_change_plan_mode({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const ufb_change_plan_mode = fb_remote_config.getValue("VUE_APP_CP_UFB_CHANGE_PLAN_MODE").asString()
                commit('SET_UFB_CHANGE_PLAN_MODE', ufb_change_plan_mode)
                return ufb_change_plan_mode
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_UFB_CHANGE_PLAN_MODE
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_UFB_CHANGE_PLAN_MODE', defaultValue)
                
                return defaultValue
            })
        },

        get_show_dashboard_widget({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const show_dashboard_widget = fb_remote_config.getValue("VUE_APP_CP_SHOW_DASHBOARD_WIDGET").asString()
                commit('SET_SHOW_DB_WIDGET', show_dashboard_widget)
                return show_dashboard_widget
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SHOW_DASHBOARD_WIDGET
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_SHOW_DB_WIDGET', defaultValue)
                
                return defaultValue
            })
        },
        
        get_show_hotspot({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const show_hotspot = fb_remote_config.getValue("VUE_APP_SHOW_HOTSPOT_PARAM").asBoolean()
                commit('SET_SHOW_HOTSPOT', show_hotspot)
                return show_hotspot
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_SHOW_HOTSPOT_PARAM
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_SHOW_HOTSPOT', defaultValue)

                return defaultValue
            })
        },

        get_poi_show_driver_license({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const poi_show_driver_license = fb_remote_config.getValue("POI_SHOW_DRIVER_LICENSE").asBoolean()
                commit('SET_POI_SHOW_DRIVER_LICENSE', poi_show_driver_license)
                return poi_show_driver_license
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.POI_SHOW_DRIVER_LICENSE
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_POI_SHOW_DRIVER_LICENSE', defaultValue)

                return defaultValue
            })
        },

        get_poi_show_passport({commit}) {
            fb_remote_config.fetchAndActivate().then(() => {
                const poi_show_passport = fb_remote_config.getValue("POI_SHOW_PASSPORT").asBoolean()
                commit('SET_POI_SHOW_PASSPORT', poi_show_passport)
                return poi_show_passport
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.POI_SHOW_PASSPORT
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_POI_SHOW_PASSPORT', defaultValue)

                return defaultValue
            })
        },

        get_is_show_esim_download_url({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_show_esim_download_url = fb_remote_config.getValue("VUE_APP_CP_IS_SHOW_ESIM_DOWNLOAD_URL").asBoolean()
                commit('SET_IS_SHOW_ESIM_DOWNLOAD_URL', is_show_esim_download_url)
                return is_show_esim_download_url
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_IS_SHOW_ESIM_DOWNLOAD_URL
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_IS_SHOW_ESIM_DOWNLOAD_URL', defaultValue)
                return defaultValue
            })
        },

        get_show_esim_activation_tab({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const show_esim_activation_tab = fb_remote_config.getValue("VUE_APP_CP_SHOW_ESIM_ACTIVATION_TAB").asBoolean()
                commit('SET_SHOW_ESIM_ACTIVATION_TAB', show_esim_activation_tab)
                return show_esim_activation_tab
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SHOW_ESIM_ACTIVATION_TAB
                console.warn('Firebase Remote Config failed, using default value:', error)
                commit('SET_SHOW_ESIM_ACTIVATION_TAB', defaultValue)
                return defaultValue
            })
        },

        get_show_esim_download_tab_on_checkout({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const show_esim_download_tab_on_checkout = fb_remote_config.getValue("VUE_APP_CP_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT").asBoolean()
                commit('SET_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT', show_esim_download_tab_on_checkout)
                return show_esim_download_tab_on_checkout
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT
                console.warn('Firebase Remote Config failed for VUE_APP_CP_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT, using default value:', error)
                commit('SET_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT', defaultValue)
                return defaultValue
            })
        },

        get_esim_download_style_on_checkout({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const raw = fb_remote_config.getValue("VUE_APP_CP_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT").asNumber()
                const esim_download_style_on_checkout = (raw === 2) ? 2 : 1
                commit('SET_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT', esim_download_style_on_checkout)
                return esim_download_style_on_checkout
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT || 1
                console.warn('Firebase Remote Config failed for VUE_APP_CP_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT, using default value:', error)
                commit('SET_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT', defaultValue)
                return defaultValue
            })
        },

        get_show_plans_password_popup({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const show_plans_password_popup = fb_remote_config.getValue("VUE_APP_CP_SHOW_PLANS_PASSWORD_POPUP").asBoolean()
                commit('SET_SHOW_PLANS_PASSWORD_POPUP', show_plans_password_popup)
                return show_plans_password_popup
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SHOW_PLANS_PASSWORD_POPUP
                console.warn('Firebase Remote Config failed for VUE_APP_CP_SHOW_PLANS_PASSWORD_POPUP, using default value:', error)
                commit('SET_SHOW_PLANS_PASSWORD_POPUP', defaultValue)
                return defaultValue
            })
        },

        get_password_style({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const raw = fb_remote_config.getValue("VUE_APP_CP_CONTROL_PASSWORD_STYLE").asNumber()
                const password_style = (raw === 2) ? 2 : 1
                commit('SET_PASSWORD_STYLE', password_style)
                return password_style
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_CONTROL_PASSWORD_STYLE || 1
                console.warn('Firebase Remote Config failed for VUE_APP_CP_CONTROL_PASSWORD_STYLE, using default value:', error)
                commit('SET_PASSWORD_STYLE', defaultValue)
                return defaultValue
            })
        },

        get_enable_invoice_line_period({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const enable_invoice_line_period = fb_remote_config.getValue("VUE_APP_CP_ENABLE_INVOICE_LINE_PERIOD").asBoolean()
                commit('SET_ENABLE_INVOICE_LINE_PERIOD', enable_invoice_line_period)
                return enable_invoice_line_period
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_ENABLE_INVOICE_LINE_PERIOD
                console.warn('Firebase Remote Config failed for VUE_APP_CP_ENABLE_INVOICE_LINE_PERIOD, using default value:', error)
                commit('SET_ENABLE_INVOICE_LINE_PERIOD', defaultValue)
                return defaultValue
            })
        },

        get_invoice_show_invoice_period({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const invoice_show_invoice_period = fb_remote_config.getValue("VUE_APP_CP_INVOICE_SHOW_INVOICE_PERIOD").asBoolean()
                commit('SET_INVOICE_SHOW_INVOICE_PERIOD', invoice_show_invoice_period)
                return invoice_show_invoice_period
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_INVOICE_SHOW_INVOICE_PERIOD
                console.warn('Firebase Remote Config failed for VUE_APP_CP_INVOICE_SHOW_INVOICE_PERIOD, using default value:', error)
                commit('SET_INVOICE_SHOW_INVOICE_PERIOD', defaultValue)
                return defaultValue
            })
        },

        get_available_sim_types({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const available_sim_types = fb_remote_config.getValue("VUE_APP_CP_AVAILABLE_SIM_TYPES").asString()
                if (!isValidSimTypes(available_sim_types)) {
                    throw new Error(`Invalid SIM types: "${available_sim_types}". Expected a comma-separated list of "esim" and/or "psim".`)
                }

                commit('SET_AVAILABLE_SIM_TYPES', available_sim_types)
                return available_sim_types
            })
            .catch((error) => {
                console.warn('Firebase Remote Config failed for VUE_APP_CP_AVAILABLE_SIM_TYPES, using fallback:', error)
                commit('SET_AVAILABLE_SIM_TYPES', FALLBACK_SIM_TYPES)
                return FALLBACK_SIM_TYPES
            })
        },

        get_sims_multi_plans_member_name({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const sims_multi_plans_member_name = fb_remote_config.getValue("VUE_APP_CP_SIMS_MULTI_PLANS_MEMBER_NAME").asBoolean()
                commit('SET_SIMS_MULTI_PLANS_MEMBER_NAME', sims_multi_plans_member_name)
                return sims_multi_plans_member_name
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_SIMS_MULTI_PLANS_MEMBER_NAME
                console.warn('Firebase Remote Config failed for VUE_APP_CP_SIMS_MULTI_PLANS_MEMBER_NAME, using default value:', error)
                commit('SET_SIMS_MULTI_PLANS_MEMBER_NAME', defaultValue)
                return defaultValue
            })
        },

        get_is_nickname_field_mandatory({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_nickname_field_mandatory = fb_remote_config.getValue("VUE_APP_CP_IS_NICKNAME_FIELD_MANDATORY").asBoolean()
                commit('SET_IS_NICKNAME_FIELD_MANDATORY', is_nickname_field_mandatory)
                return is_nickname_field_mandatory
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_IS_NICKNAME_FIELD_MANDATORY
                console.warn('Firebase Remote Config failed for VUE_APP_CP_IS_NICKNAME_FIELD_MANDATORY, using default value:', error)
                commit('SET_IS_NICKNAME_FIELD_MANDATORY', defaultValue)
                return defaultValue
            })
        },

        get_is_trial_mode({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_trial_mode = fb_remote_config.getValue("VUE_APP_CP_IS_TRIAL_MODE").asBoolean()
                commit('SET_IS_TRIAL_MODE', is_trial_mode)
                return is_trial_mode
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_IS_TRIAL_MODE
                console.warn('Firebase Remote Config failed for VUE_APP_CP_IS_TRIAL_MODE, using default value:', error)
                commit('SET_IS_TRIAL_MODE', defaultValue)
                return defaultValue
            })
        },
        get_is_enable_purchase_plans_and_addons({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_enable_purchase_plans_and_addons = fb_remote_config.getValue("VUE_APP_CP_ENABLE_PURCHASE_PLANS_AND_ADDONS").asBoolean()
                commit('SET_IS_ENABLE_PURCHASE_PLANS_AND_ADDONS', is_enable_purchase_plans_and_addons)
                return is_enable_purchase_plans_and_addons
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_ENABLE_PURCHASE_PLANS_AND_ADDONS
                console.warn('Firebase Remote Config failed for VUE_APP_CP_ENABLE_PURCHASE_PLANS_AND_ADDONS, using default value:', error)
                commit('SET_IS_ENABLE_PURCHASE_PLANS_AND_ADDONS', defaultValue)
                return defaultValue
            })
        },

        get_is_travel_esim_enabled({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const is_travel_esim_enabled = fb_remote_config.getValue("VUE_APP_CP_TRAVEL_ESIM_ENABLED").asBoolean()
                commit('SET_IS_TRAVEL_ESIM_ENABLED', is_travel_esim_enabled)
                return is_travel_esim_enabled
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_TRAVEL_ESIM_ENABLED
                console.warn('Firebase Remote Config failed for VUE_APP_CP_TRAVEL_ESIM_ENABLED, using default value:', error)
                commit('SET_IS_TRAVEL_ESIM_ENABLED', defaultValue)
                return defaultValue
            })
        },

        get_available_sso_types({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const available_sso_types = fb_remote_config.getValue("VUE_APP_CP_AVAILABLE_SSO_TYPES").asString()
                if (!isValidSsoTypes(available_sso_types)) {
                    throw new Error(`Invalid SSO types: "${available_sso_types}". Expected "none" or a comma-separated list of "google" and/or "apple".`)
                }

                commit('SET_AVAILABLE_SSO_TYPES', available_sso_types)
                return available_sso_types
            })
            .catch((error) => {
                console.warn('Firebase Remote Config failed for VUE_APP_CP_AVAILABLE_SSO_TYPES, using fallback:', error)
                commit('SET_AVAILABLE_SSO_TYPES', FALLBACK_SSO_TYPES)
                return FALLBACK_SSO_TYPES
            })
        },

        get_show_usage_breakdown({commit}) {
            return fb_remote_config.fetchAndActivate().then(() => {
                const show_usage_breakdown = fb_remote_config.getValue("VUE_APP_CP_ENABLE_USAGE_BREAKDOWN").asBoolean()
                commit('SET_SHOW_USAGE_BREAKDOWN', show_usage_breakdown)
                return show_usage_breakdown
            })
            .catch((error) => {
                const defaultValue = fb_remote_config.defaultConfig.VUE_APP_CP_ENABLE_USAGE_BREAKDOWN
                console.warn('Firebase Remote Config failed for VUE_APP_CP_ENABLE_USAGE_BREAKDOWN, using default value:', error)
                commit('SET_SHOW_USAGE_BREAKDOWN', defaultValue)
                return defaultValue
            })
        },
    },
    

    mutations: {
        SET_IS_HIDE_PREPAY_TOPUP(state, is_hide_prepay_topup) {
            state.is_hide_prepay_topup = is_hide_prepay_topup
        },

        SET_IS_HIDE_PREPAY_BALANCE_IF_ZERO(state, is_hide_prepay_balance_if_zero) {
            state.is_hide_prepay_balance_if_zero = is_hide_prepay_balance_if_zero
        },

        SET_IS_AFTERPAY_PAYMENT_MODULE_ALLOWED(state, is_afterpay_payment_module_allowed) {
            state.is_afterpay_payment_module_allowed_fb = is_afterpay_payment_module_allowed
        },

        SET_IS_ENABLE_PROMO_CODE_MENU_ITEM(state, is_enable_promo_code_menu_item) {
            state.is_enable_promo_code_menu_item = is_enable_promo_code_menu_item 
        },

        SET_IS_KLARNA_PAYMENT_MODULE_ALLOWED(state, is_klarna_payment_module_allowed) {
            state.is_klarna_payment_module_allowed = is_klarna_payment_module_allowed
        },
        SET_SHOW_ADDON_IMAGES(state, show_addon_images) {
            state.show_addon_images = show_addon_images
        },
        
        SET_SHOW_CARRYOVER(state, show_carryover) {
            state.show_carryover = show_carryover
        },
        
        SET_IS_MODULE_UFB_ENABLED(state, is_module_ufb_enabled) {
            state.is_module_ufb_enabled = is_module_ufb_enabled
        },

        SET_UFB_CHANGE_PLAN_MODE(state, ufb_change_plan_mode) {
            state.ufb_change_plan_mode = ufb_change_plan_mode
        },

        SET_SHOW_DB_WIDGET(state, show_dashboard_widget) {
            state.show_dashboard_widget = show_dashboard_widget
        },

        SET_SHOW_HOTSPOT(state, show_hotspot) {
            state.show_hotspot = show_hotspot
        },

        // POI
        SET_POI_SHOW_DRIVER_LICENSE(state, poi_show_driver_license) {
            state.poi_show_driver_license = poi_show_driver_license
        },

        SET_POI_SHOW_PASSPORT(state, poi_show_passport) {
            state.poi_show_passport = poi_show_passport
        },

        SET_SHOW_PLANS_PASSWORD_POPUP(state, show_plans_password_popup) {
            state.show_plans_password_popup = show_plans_password_popup
        },

        SET_PASSWORD_STYLE(state, password_style) {
            state.password_style = password_style
        },

        SET_ENABLE_INVOICE_LINE_PERIOD(state, enable_invoice_line_period) {
            state.enable_invoice_line_period = enable_invoice_line_period
        },

        SET_IS_SHOW_ESIM_DOWNLOAD_URL(state, is_show_esim_download_url) {
            state.is_show_esim_download_url = is_show_esim_download_url
        },

        SET_SHOW_ESIM_ACTIVATION_TAB(state, show_esim_activation_tab) {
            state.show_esim_activation_tab = show_esim_activation_tab
        },

        SET_SHOW_ESIM_DOWNLOAD_TAB_ON_CHECKOUT(state, show_esim_download_tab_on_checkout) {
            state.show_esim_download_tab_on_checkout = show_esim_download_tab_on_checkout
        },

        SET_ESIM_DOWNLOAD_STYLE_ON_CHECKOUT(state, esim_download_style_on_checkout) {
            state.esim_download_style_on_checkout = esim_download_style_on_checkout
        },

        SET_INVOICE_SHOW_INVOICE_PERIOD(state, invoice_show_invoice_period) {
            state.invoice_show_invoice_period = invoice_show_invoice_period
        },

        SET_AVAILABLE_SIM_TYPES(state, available_sim_types) {
            state.available_sim_types = available_sim_types
        },

        SET_SIMS_MULTI_PLANS_MEMBER_NAME(state, sims_multi_plans_member_name) {
            state.sims_multi_plans_member_name = sims_multi_plans_member_name
        },

        SET_IS_NICKNAME_FIELD_MANDATORY(state, is_nickname_field_mandatory) {
            state.is_nickname_field_mandatory = is_nickname_field_mandatory
        },

        SET_IS_TRIAL_MODE(state, is_trial_mode) {
            state.is_trial_mode = is_trial_mode
        },

        SET_IS_ENABLE_PURCHASE_PLANS_AND_ADDONS(state, is_enable_purchase_plans_and_addons) {
            state.is_enable_purchase_plans_and_addons = is_enable_purchase_plans_and_addons
        },

        SET_IS_TRAVEL_ESIM_ENABLED(state, is_travel_esim_enabled) {
            state.is_travel_esim_enabled = is_travel_esim_enabled
        },

        SET_AVAILABLE_SSO_TYPES(state, available_sso_types) {
            state.available_sso_types = available_sso_types
        },

        SET_SHOW_USAGE_BREAKDOWN(state, show_usage_breakdown) {
            state.show_usage_breakdown = show_usage_breakdown
        },
    },
};
